import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgents } from '@/lib/api/agents';
import { ApiError } from '@/lib/api/client';
import { useCreateSession } from '@/lib/api/sessions';

export function SessionNewPage() {
  const agentsQuery = useAgents({ page: 1, pageSize: 100 });
  const createSession = useCreateSession();
  const navigate = useNavigate();

  const [agentId, setAgentId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!agentId) {
      setError('Pick an agent for this session.');
      return;
    }
    setError(null);
    createSession.mutate(
      { agentId, name: name.trim() || undefined },
      {
        onSuccess: (session) =>
          void navigate({ to: '/sessions/$sessionId', params: { sessionId: session.id } }),
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not create the session.'),
      },
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">New session</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Pins the agent's active version so the transcript stays reproducible.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Session</CardTitle>
          <CardDescription>Choose the agent to trace and test.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="agent">Agent</Label>
              <Select required value={agentId} onValueChange={setAgentId}>
                <SelectTrigger id="agent">
                  <SelectValue placeholder="Select an agent…" />
                </SelectTrigger>
                <SelectContent>
                  {agentsQuery.data?.items.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.activeVersion.name} (v{agent.activeVersion.versionNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                maxLength={120}
                placeholder="Smoke test — greeting"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={createSession.isPending}>
                {createSession.isPending ? 'Creating…' : 'Create session'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: '/sessions' })}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
